import React, { FC, useMemo } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { ThemeProvider as StyledThemeProvider, useTheme } from 'styled-components';
import {
  EnterpriseDarkTheme,
  EnterpriseTheme,
  PCTheme,
  shouldUseStudioTheme,
  StudioTheme,
  StudioDarkTheme,
  Theme,
} from '@minecraft.themes';
import { ThemeEnum, ThemeSwitcherProvider, useThemeSwitcherContext } from '@minecraft.theme-switcher';
import { gqlSystemRoleCode } from '@minecraft.graphql-types';
import useSystemRole from 'src/hooks/useSystemRole';
import { isPC, isStudio } from 'shared/utils/systemRoleUtils';

export const getCurrentTheme = ({
  systemRole,
  isDarkTheme,
}: {
  systemRole: gqlSystemRoleCode;
  isDarkTheme: boolean;
}): Theme => {
  if (shouldUseStudioTheme() || isStudio(systemRole)) {
    if (isDarkTheme) {
      return StudioDarkTheme;
    }

    return StudioTheme;
  }

  if (isPC(systemRole)) {
    return PCTheme;
  }

  if (isDarkTheme) {
    return EnterpriseDarkTheme;
  }

  return EnterpriseTheme;
};

const WrappedThemeProvider: FC = ({ children }) => {
  const systemRole = useSystemRole();
  const { isDarkTheme, isLightTheme } = useThemeSwitcherContext();

  const currentTheme = useMemo(() => getCurrentTheme({ systemRole, isDarkTheme }), [systemRole, isDarkTheme]);

  return <StyledThemeProvider theme={{ isDarkTheme, isLightTheme, ...currentTheme }}>{children}</StyledThemeProvider>;
};

const SkeletonThemeProvider: FC = ({ children }) => {
  const theme = useTheme();

  return (
    <SkeletonTheme
      baseColor={theme.designSystem.bgColors.pageBody}
      highlightColor={theme.designSystem.bgColors.listItem}
    >
      {children}
    </SkeletonTheme>
  );
};

type Props = {
  defaultTheme?: ThemeEnum;
};
const ThemeProvider: FC<Props> = ({ defaultTheme, children }) => (
  <ThemeSwitcherProvider defaultTheme={defaultTheme}>
    <WrappedThemeProvider>
      <SkeletonThemeProvider>{children}</SkeletonThemeProvider>
    </WrappedThemeProvider>
  </ThemeSwitcherProvider>
);

export default ThemeProvider;
